import { CommonModule } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { AbstractControl, ControlContainer, FormArray, FormGroup, FormGroupDirective, ReactiveFormsModule } from "@angular/forms";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { map, Observable, of } from "rxjs";
import { ExpenseType, TripLegsDto } from "../../../dashboard/models/trip-request-models";
import { IntegerInputDirective } from "../../../shared/directives/integer-input.directive";
import { TouchedErrorStateMatcher } from "../../../shared/other/touched-form-error-state-matcher";
import { LookupsService } from "../../../shared/services/lookups-service";


@Component({
  selector: 'app-agent-trip-summary',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatIconModule,
    MatExpansionModule,
    MatCheckbox,
    IntegerInputDirective,
    MatRadioModule,
  ],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  templateUrl: './agent-trip-summary.component.html',
  styleUrl: './agent-trip-summary.component.scss',
})
export class AgentTripsummaryComponent implements OnInit {
    tripRequest!: FormGroup;
    appointmentDatesFr!: FormArray;
    addressFr!: FormArray;
    private postTripRequestFormDir = inject(FormGroupDirective);
    private lookupService = inject(LookupsService);
    matcher = new TouchedErrorStateMatcher();


    ngOnInit(): void {
        this.tripRequest = this.postTripRequestFormDir.form.get("tripRequest") as FormGroup;
        this.appointmentDatesFr = this.tripRequest.get("tripAppointments") as FormArray;
        this.addressFr = this.tripRequest.get("tripAddresses") as FormArray;
    }

    getAppointmentLegs(index: number): FormArray {
        return this.appointmentDatesFr.at(index).get('tripLegs') as FormArray;
    }

    getAddressCategory(index: number) : Observable<string> {
        if (index > -1) {
            const otherCategoryType = this.addressFr.at(index).get('otherCategoryType')?.value;
            if (otherCategoryType === null || otherCategoryType === '') {
                return this.lookupService.getAddressCategories()
                    .pipe(
                        map(list => {
                            const item = list.find(x => x.categoryId === Number(this.addressFr.at(index).get('addressCategoryId')?.value));
                            return item?.categoryName ?? '';
                        })
                );
            }
            else {
                return of(otherCategoryType);
            }
        }
        return of('');
    }

    getAddress(index: number) : string {
        return (index > -1) ? this.addressFr.at(index).get('address')?.value : '';
    }

    onMilesEntered(appIndex: number) {
        const legFr = this.getAppointmentLegs(appIndex) as FormArray;
        const hasDifferentValue = legFr.value.some((item: TripLegsDto) => Number(item.shortestMiles) !== Number(item.enteredMiles));
        this.appointmentDatesFr.at(appIndex).get('showExplanationText')?.setValue(hasDifferentValue);
        if (!hasDifferentValue) {
            this.appointmentDatesFr.at(appIndex).get('explanationText')?.setValue('');
        }
    }

    getAddressTitle(index: number) : string {
        if (index < 0) return '';
        if (index === 0) {
            return "Starting Location";
        }
        return "Stop Location";
    }

    hasError(controlName: string, errorName: string): boolean {
        const control: AbstractControl | null = this.tripRequest.get(`${controlName}`);
        if (controlName === 'tripAuthorize') {
            return !!control && (control.hasError(errorName) || !control.value) && (control.dirty || control.touched);
        }
        else {
            return !!control && control.hasError(errorName) && (control.dirty || control.touched);
        }  
    }

    getExpenseAmount(index: number, expenseType: string) : number {
        if (index < 0) return 0;
        const expense = this.appointmentDatesFr.at(index).get('tripExpenses') as FormArray;
        if (expense) {
            if (expenseType === ExpenseType.Toll) {
                return Number(expense.at(0).get('expenseAmount')?.value)
            }
            else {
                return Number(expense.at(1).get('expenseAmount')?.value)
            }
        }
        return 0;
    }

    getTotalAmount(index: number) {
       if (index < 0) return 0;
       const toll = Number(this.getExpenseAmount(index, ExpenseType.Toll));
       const parking = Number(this.getExpenseAmount(index,ExpenseType.Parking));
       const MileageAmount = Number(this.appointmentDatesFr.at(index).get('totalMileageAmount')?.value)
       const expenseAmount = toll + parking + MileageAmount;
       this.appointmentDatesFr.at(index).get('expenseAmount')?.setValue(expenseAmount);
       return expenseAmount;

    }

    getTripTotalMailes() : number {
        return this.appointmentDatesFr.value.reduce((acc: number, curr: any) => {
            return acc + (+curr.totalMiles || 0);
        }, 0);
    }

    getTripTotalMileageAmount() : number {
        return this.appointmentDatesFr.value.reduce((acc: number, curr: any) => {
            return acc + (+curr.totalMileageAmount || 0);
        }, 0);
    }

    getTripExpenseAmount(expenseType: string) : number {
        let total = 0;
        this.appointmentDatesFr.controls.forEach(appointment => {
            const expense = appointment.get('tripExpenses') as FormArray;
            if (expenseType === ExpenseType.Toll) {
                total = total + Number(expense.at(0).get('expenseAmount')?.value);
            }
            else {
                total = total + Number(expense.at(1).get('expenseAmount')?.value);
            }
            
        });
        return total;
    }

    getTripTotalAmount() : number {
        return this.getTripTotalMileageAmount() + this.getTripExpenseAmount('Toll') + this.getTripExpenseAmount('Parking');
    }

}